import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowLeft, ArrowRight, Megaphone, Sparkles, MailCheck } from "lucide-react";

const CREATOR_TYPES = ["Influencer", "UGC Agency", "Performance Marketing Agency"];

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

function errMsg(d) {
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
}

export function AuthModal() {
  const { authOpen, closeAuth, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup | forgot | verify-sent
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(null); // promoter | influencer
  const [accountType, setAccountType] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setMode("signin"); setName(""); setEmail(""); setPassword(""); setRole(null); setAccountType(null); setBusy(false); };
  const onOpenChange = (o) => { if (!o) { closeAuth(); reset(); } };

  const afterAuth = (data) => {
    setUser(data);
    toast.success("Signed in!");
    closeAuth(); reset();
    navigate(data.profile_complete ? "/dashboard" : "/onboarding");
  };

  const submitSignin = async () => {
    if (!email || !password) { toast.error("Enter your email and password"); return; }
    setBusy(true);
    try { const { data } = await api.post("/auth/login", { email, password }); afterAuth(data); }
    catch (e) {
      if (e?.response?.status === 403) setMode("verify-sent");
      toast.error(errMsg(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitSignup = async () => {
    if (!role) { toast.error("Please choose how you'll use InfluencerCollab"); return; }
    if (role === "influencer" && !accountType) { toast.error("Please select your creator account type"); return; }
    if (!name.trim()) { toast.error("Enter your name"); return; }
    if (!email) { toast.error("Enter your email"); return; }
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/signup", {
        name, email, password, role,
        account_type: role === "influencer" ? accountType : null,
        base_url: window.location.origin,
      });
      if (window.fbq) { window.fbq("track", "CompleteRegistration"); }
      setMode("verify-sent");
    } catch (e) { toast.error(errMsg(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  const submitForgot = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim(), base_url: window.location.origin });
      toast.success("If an account exists, a reset link is on its way to your inbox.");
      setMode("signin");
    } catch (e) { toast.error(errMsg(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  const resendVerification = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { toast.error("Enter the email you signed up with"); return; }
    setBusy(true);
    try {
      await api.post("/auth/resend-verification", { email: email.trim(), base_url: window.location.origin });
      toast.success("Verification link sent again. Check your inbox.");
    } catch (e) { toast.error(errMsg(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  const titles = { signin: "Welcome back", signup: "Create your account", forgot: "Reset your password", "verify-sent": "Verify your email" };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const roleCard = (key, Icon, title, desc) => {
    const active = role === key;
    return (
      <button type="button" key={key} onClick={() => { setRole(key); if (key !== "influencer") setAccountType(null); }}
        data-testid={`auth-role-${key}`}
        className={`text-left rounded-2xl border p-4 transition-colors ${active ? "border-brand-pink bg-brand-pink/10 ring-2 ring-brand-pink" : "border-border bg-background hover:bg-surfaceHover"}`}>
        <Icon className={`h-6 w-6 mb-2 ${active ? "text-brand-pink" : "text-muted-foreground"}`} />
        <p className="font-heading font-bold text-sm">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </button>
    );
  };

  return (
    <Dialog open={authOpen} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{titles[mode]}</DialogTitle>
          <DialogDescription className="sr-only">Sign in, create an account, or reset your InfluencerCollab password.</DialogDescription>
        </DialogHeader>

        {mode === "verify-sent" ? (
          <div className="text-center py-4" data-testid="auth-verify-sent">
            <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-brand-pink/10 flex items-center justify-center">
              <MailCheck className="h-7 w-7 text-brand-pink" />
            </div>
            <p className="font-heading font-bold text-lg mb-1">Check your inbox</p>
            <p className="text-sm text-muted-foreground mb-5">
              We've sent a verification link{email ? <> to <span className="font-semibold text-foreground">{email}</span></> : ""}.
              Click it to activate your account, then sign in.
            </p>
            <Button onClick={resendVerification} disabled={busy} data-testid="auth-resend-verify-btn" variant="outline" className="rounded-full h-11 px-6 border-border">
              {busy ? "Sending…" : "Resend verification link"}
            </Button>
            <button onClick={() => setMode("signin")} data-testid="auth-back-signin" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mx-auto mt-5">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {mode !== "forgot" && (
              <>
                <Button type="button" variant="outline" onClick={googleLogin} data-testid="auth-google-btn" className="w-full h-12 rounded-full border-border bg-background hover:bg-surfaceHover font-semibold">
                  <GoogleIcon /> <span className="ml-2">Continue with Google</span>
                </Button>
                <div className="flex items-center gap-3 py-1">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">or continue with email</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              </>
            )}
            {mode === "signup" && (
              <div>
                <Label className="mb-2 block">I'm joining as…</Label>
                <div className="grid grid-cols-2 gap-3">
                  {roleCard("influencer", Sparkles, "Creator", "Find campaigns & bid")}
                  {roleCard("promoter", Megaphone, "Brand / Promoter", "Post campaigns & hire")}
                </div>
                {role === "influencer" && (
                  <div className="mt-3">
                    <Label className="mb-2 block text-xs text-muted-foreground">What type of creator are you?</Label>
                    <div className="flex flex-wrap gap-2">
                      {CREATOR_TYPES.map((t) => {
                        const active = accountType === t;
                        return (
                          <button type="button" key={t} onClick={() => setAccountType(t)}
                            data-testid={`auth-accounttype-${t.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-brand-pink text-white border-brand-pink font-semibold" : "border-border bg-background text-muted-foreground hover:text-foreground"}`}>
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {mode === "signup" && (
              <div>
                <Label className="mb-2 block">Full name</Label>
                <div className="relative">
                  <User className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-name-input" placeholder="Your name" className="bg-background border-border h-12 pl-9" />
                </div>
              </div>
            )}

            <div>
              <Label className="mb-2 block">Email address</Label>
              <div className="relative">
                <Mail className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-email-input" type="email" placeholder="you@example.com" className="bg-background border-border h-12 pl-9" />
              </div>
            </div>

            {mode !== "forgot" && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Password</Label>
                  {mode === "signin" && (
                    <button onClick={() => setMode("forgot")} data-testid="auth-forgot-link" className="text-xs text-brand-pink hover:opacity-70 font-semibold">Forgot password?</button>
                  )}
                </div>
                <div className="relative">
                  <Lock className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-password-input" type="password"
                    placeholder={mode === "signup" ? "At least 8 characters" : "Your password"} className="bg-background border-border h-12 pl-9"
                    onKeyDown={(e) => e.key === "Enter" && (mode === "signup" ? submitSignup() : submitSignin())} />
                </div>
              </div>
            )}

            {mode === "signin" && (
              <Button onClick={submitSignin} disabled={busy} data-testid="auth-signin-btn" className="w-full h-12 rounded-full bg-brand-gradient text-white font-bold">
                {busy ? "Signing in…" : "Sign in"} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {mode === "signup" && (
              <Button onClick={submitSignup} disabled={busy} data-testid="auth-signup-btn" className="w-full h-12 rounded-full bg-brand-gradient text-white font-bold">
                {busy ? "Creating…" : "Create account"} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {mode === "forgot" && (
              <>
                <Button onClick={submitForgot} disabled={busy} data-testid="auth-forgot-btn" className="w-full h-12 rounded-full bg-brand-gradient text-white font-bold">
                  {busy ? "Sending…" : "Email me a reset link"}
                </Button>
                <button onClick={() => setMode("signin")} data-testid="auth-back-signin" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mx-auto">
                  <ArrowLeft className="h-4 w-4" /> Back to sign in
                </button>
              </>
            )}

            {mode !== "forgot" && (
              <div className="pt-1 space-y-2">
                <p className="text-sm text-muted-foreground text-center">{mode === "signin" ? "New to InfluencerCollab?" : "Already have an account?"}</p>
                <Button variant="outline" onClick={() => setMode(mode === "signin" ? "signup" : "signin")} data-testid="auth-switch-mode"
                  className="w-full h-12 rounded-full border-2 border-brand-pink text-brand-pink font-bold hover:bg-brand-pink/10">
                  {mode === "signin" ? "Create an account" : "Sign in"}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
